// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import MasterSelect from "@/components/MasterSelect";

const STAGES = [
  { key: "qualification", label: "Qualification" },
  { key: "proposal", label: "Proposal" },
  { key: "negotiation", label: "Negotiation" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", contact_name: "", contact_email: "", company: "", stage: "qualification", value_gbp: 0, close_date: "", notes: "" });

  const [lostDialog, setLostDialog] = useState({ open: false, deal: null, reason: "" });

  const load = useCallback(async () => {
    const r = await api.get("/crm/deals");
    setDeals(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.post("/crm/deals", { ...form, value_gbp: parseFloat(form.value_gbp || 0) });
      toast.success("Deal created");
      setOpen(false);
      setForm({ title: "", contact_name: "", contact_email: "", company: "", stage: "qualification", value_gbp: 0, close_date: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStage = async (d, stage) => {
    // Special case — moving to Lost requires a reason from `reason_code` master
    if (stage === "lost") {
      setLostDialog({ open: true, deal: d, reason: "" });
      return;
    }
    await api.post(`/crm/deals/${d.id}/stage`, { stage });
    load();
  };
  const confirmLost = async () => {
    if (!lostDialog.reason.trim()) {
      toast.error("Please pick or type a lost reason");
      return;
    }
    try {
      await api.post(`/crm/deals/${lostDialog.deal.id}/stage`, {
        stage: "lost",
        lost_reason: lostDialog.reason,
      });
      toast.success("Deal marked as lost");
      setLostDialog({ open: false, deal: null, reason: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };
  const del = async (d) => {
    if (!window.confirm("Delete deal?")) return;
    await api.delete(`/crm/deals/${d.id}`);
    load();
  };

  const totalValue = deals.reduce((s, d) => s + (d.value_gbp || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Deals</h2>
          <div className="text-xs text-muted-foreground">Total pipeline value: £{totalValue.toLocaleString()}</div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="crm-new-deal-btn">
          <Plus className="w-4 h-4 mr-2" /> New deal
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {STAGES.map(s => {
          const items = deals.filter(d => d.stage === s.key);
          const val = items.reduce((sum, d) => sum + (d.value_gbp || 0), 0);
          return (
            <div key={s.key} className="rounded-xl border border-border bg-card p-3 min-h-[200px]" data-testid={`deal-col-${s.key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{s.label}</span>
                <Badge variant="secondary">{items.length}</Badge>
              </div>
              <div className="text-xs text-muted-foreground mb-2">£{val.toLocaleString()}</div>
              <div className="space-y-2">
                {items.map(d => (
                  <div key={d.id} data-testid={`deal-card-${d.id}`} className="rounded-lg bg-muted/50 p-3 border border-border/50 space-y-1">
                    <div className="font-medium text-sm">{d.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{d.contact_name || d.company}</div>
                    <div className="text-xs font-semibold text-primary">£{Number(d.value_gbp || 0).toLocaleString()}</div>
                    <div className="flex gap-1 pt-1 flex-wrap">
                      <Select value={d.stage} onValueChange={(v) => setStage(d, v)}>
                        <SelectTrigger className="h-7 text-xs w-full"><SelectValue /></SelectTrigger>
                        <SelectContent>{STAGES.map(x => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" onClick={() => del(d)} className="h-7 w-7">
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">No deals</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New deal</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="deal-form-title" /></div>
            <div><Label className="text-xs">Contact name</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
            <div><Label className="text-xs">Contact email</Label><Input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} /></div>
            <div><Label className="text-xs">Company</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} /></div>
            <div><Label className="text-xs">Value £</Label><Input type="number" value={form.value_gbp} onChange={e => setForm({...form, value_gbp: e.target.value})} /></div>
            <div><Label className="text-xs">Stage</Label>
              <Select value={form.stage} onValueChange={v => setForm({...form, stage: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Close date</Label><Input type="date" value={form.close_date} onChange={e => setForm({...form, close_date: e.target.value})} /></div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="deal-form-save">Create deal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lost-reason dialog */}
      <Dialog open={lostDialog.open} onOpenChange={(o) => !o && setLostDialog({ open: false, deal: null, reason: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display font-light text-2xl">Mark deal as lost</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Please capture why you lost <strong>{lostDialog.deal?.title}</strong>. This feeds back into sales analytics.
            </p>
            <Label className="text-xs">Reason</Label>
            <MasterSelect
              entityType="lost_reason"
              value={lostDialog.reason}
              onChange={(v) => setLostDialog((d) => ({ ...d, reason: v }))}
              placeholder="Choose a reason"
              testId="deal-lost-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostDialog({ open: false, deal: null, reason: "" })}>Cancel</Button>
            <Button onClick={confirmLost} data-testid="deal-lost-confirm">Confirm lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
